import { useState } from "react";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { MapPin, Users, Target, Award } from "lucide-react";
import WaitlistModal from "@/components/WaitlistModal";

const About = () => {
  const [waitlistModalOpen, setWaitlistModalOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      {/* Hero Section */}
      <section className="py-20 bg-gradient-mountain text-white">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            Built by Off-Roaders, 
            <span className="text-primary-glow"> For Off-Roaders</span>
          </h1>
          <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto leading-relaxed">
            We're passionate explorers who believe the best adventures happen off the beaten path. 
            Airing Down was born from countless weekends on the trails and a vision to connect 
            our community like never before.
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">Our Mission</h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              To create the ultimate platform that motivates people to get outdoors, explore new trails, 
              and build lasting connections within the off-road community. We go beyond navigation—we 
              provide the tools, insights, and social connections that make every adventure memorable.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="text-center">
              <CardHeader>
                <div className="mx-auto w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <MapPin className="w-6 h-6 text-primary" />
                </div>
                <CardTitle className="text-xl">Explore</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription>
                  Discover hidden trails and new adventures through our community-driven platform
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="text-center">
              <CardHeader>
                <div className="mx-auto w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <Users className="w-6 h-6 text-primary" />
                </div>
                <CardTitle className="text-xl">Connect</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription>
                  Build relationships with fellow off-roaders and join organized events and rides
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="text-center">
              <CardHeader>
                <div className="mx-auto w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <Target className="w-6 h-6 text-primary" />
                </div>
                <CardTitle className="text-xl">Track</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription>
                  Log your adventures, monitor progress, and achieve personal milestones
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="text-center">
              <CardHeader>
                <div className="mx-auto w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <Award className="w-6 h-6 text-primary" />
                </div>
                <CardTitle className="text-xl">Achieve</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription>
                  Earn badges, maintain streaks, and compete in friendly challenges
                </CardDescription>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 bg-muted/50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-16">What Drives Us</h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <h3 className="text-xl font-semibold mb-4 text-primary">Community First</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Every feature we build puts the off-road community at the center. We believe 
                  the best trail insights come from real riders sharing real experiences.
                </p>
              </div>

              <div className="text-center">
                <h3 className="text-xl font-semibold mb-4 text-primary">Authentic Adventure</h3>
                <p className="text-muted-foreground leading-relaxed">
                  We're not just building an app—we're creating a movement that encourages 
                  people to disconnect from screens and reconnect with nature.
                </p>
              </div>

              <div className="text-center">
                <h3 className="text-xl font-semibold mb-4 text-primary">Responsible Exploration</h3>
                <p className="text-muted-foreground leading-relaxed">
                  We promote Leave No Trace principles and responsible off-roading practices 
                  to preserve our trails for future generations.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-8">The Story</h2>
          <div className="max-w-4xl mx-auto">
            <p className="text-lg text-muted-foreground leading-relaxed mb-6">
              Airing Down was born from a simple desire: to share the beauty of Southern California's 
              mountains and deserts with family and friends. Weekend after weekend, we'd load up and 
              head for the trails—but planning each adventure felt like solving a puzzle with missing 
              pieces. Which trails could our rigs actually handle? How long would it take? Was there 
              somewhere amazing just over the next ridge that we didn't know about?
            </p>
            <p className="text-lg text-muted-foreground leading-relaxed mb-6">
              The answers were scattered across forums, buried in outdated posts, or hidden in hour-long 
              YouTube videos where you'd have to scrub through to find that one piece of useful info. We 
              knew there had to be a better way—a single place where our community could share what we 
              know, help each other discover new adventures, and make it easier for anyone to experience 
              the joy of exploring off the beaten path.
            </p>
            <p className="text-lg text-muted-foreground leading-relaxed mb-12">
              That's why we built Airing Down: to be the resource we wish we had when we first aired 
              down our tires and pointed toward the mountains. Whether you're loading up the family for 
              your first forest road or you're a seasoned explorer looking for your next challenge, this 
              platform is for you.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button variant="hero" size="lg" onClick={() => setWaitlistModalOpen(true)}>
                Join Early Access
              </Button>
              <Button variant="adventure" size="lg" onClick={() => window.location.href = '/explore'}>
                Explore Trails
              </Button>
            </div>
            
            {/* Social Links */}
            <div className="mt-8 flex items-center justify-center gap-6">
              <span className="text-muted-foreground">Follow our journey:</span>
              <a 
                href="https://instagram.com/airing_down" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
                aria-label="Follow us on Instagram"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect width="20" height="20" x="2" y="2" rx="5" ry="5"/>
                  <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
                  <line x1="17.5" x2="17.51" y1="6.5" y2="6.5"/>
                </svg>
                <span className="text-sm">Instagram</span>
              </a>
              <a 
                href="https://tiktok.com/@airing.down" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
                aria-label="Follow us on TikTok"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                </svg>
                <span className="text-sm">TikTok</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      <WaitlistModal 
        open={waitlistModalOpen} 
        onOpenChange={setWaitlistModalOpen} 
      />
    </div>
  );
};

export default About;